import Accordion, { AccordionItem } from '@storeblocks/accordion';
import { type FC } from 'react';
import styled from 'styled-components';

import { useFundListQuery } from '@/api/funds/fundsApi';
import { Order } from '@/api/orderList/models/Order';
import { ConnectSurface } from '@/components/Pretzel/ConnectSurface';
import { WithGap } from '@/components/WithGap';
import { useTexts } from '@/hooks/useTexts';
import { fmsWithTemplate } from '@/i18n/fmsWithTemplate';
import { useUserLocale } from '@/i18n/hooks';
import { formatNumber } from '@/util/formatNumber';

import { getPaymentInfoForOrderLines } from '../utils/getPaymentInfoForOrderLines';
import { ForeignPayment } from './ForeignPayment';

interface Props {
  order: Order;
}

export const PaymentInformation: FC<Props> = ({ order }) => {
  const texts = useTexts();
  const locale = useUserLocale();
  const { data: fundList } = useFundListQuery();

  const payment = getPaymentInfoForOrderLines(order.orderLines, fundList!);

  return (
    <ConnectSurface>
      <WithGap gap="8">
        <h3>{texts.pages.orderList.settlement.title}</h3>

        <p className="font-body">
          {texts.pages.orderList.settlement.settlementAccount}
        </p>

        <ul>
          {payment.map((payment) => (
            <li
              key={`${payment.paymentInfo.bankAccountNumber}-${payment.paymentInfo.currency}`}
            >
              <span className="font-body">
                {fmsWithTemplate(
                  texts.pages.orderList.settlement.transferAmount,
                  {
                    amount: formatNumber(
                      payment.totalAmount,
                      locale,
                      payment.paymentInfo.currency,
                    ),
                    accountNumber: payment.paymentInfo.bankAccountNumber,
                  },
                )}
              </span>
            </li>
          ))}
        </ul>

        <p className="font-body">
          {texts.pages.orderList.settlement.markWithOrganizationName}
        </p>
      </WithGap>

      <StyledAccordion headingsAriaLevel={4}>
        <AccordionItem
          id="foreign-payment"
          header={texts.pages.orderList.settlement.foreignPayment.title}
        >
          <WithGap>
            {payment.map((payment) => (
              <ForeignPayment
                key={`${payment.paymentInfo.bankAccountNumber}-${payment.paymentInfo.currency}`}
                amount={payment.totalAmount}
                paymentInfo={payment.paymentInfo}
              />
            ))}
          </WithGap>
        </AccordionItem>
      </StyledAccordion>
    </ConnectSurface>
  );
};

const StyledAccordion = styled(Accordion)`
  margin-top: 24px;

  button {
    background-color: transparent;
  }
`;
